import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AdminSettings() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: () =>
      api.get("/settings").then((r) => r.data),
  });

  const [form, setForm] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const s = form || data || {};

  const setF = (key, value) => {
    setForm({
      ...(form || data || {}),
      [key]: value,
    });
  };

  const setSocial = (key, value) => {
    setForm({
      ...(form || data || {}),
      social: {
        ...((s && s.social) || {}),
        [key]: value,
      },
    });
  };

  const setSeo = (key, value) => {
    setForm({
      ...(form || data || {}),
      seo: {
        ...((s && s.seo) || {}),
        [key]: value,
      },
    });
  };

  const save = useMutation({
    mutationFn: (payload) =>
      api.put("/admin/settings", payload).then((r) => r.data),

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["settings"],
      });

      toast.success("Settings saved. Site updated.");
    },

    onError: (e) => {
      toast.error(
        formatApiError(
          e.response &&
            e.response.data &&
            e.response.data.detail
        ) || "Save failed"
      );
    },
  });

  /*
   * LOGO UPLOAD
   */
  const uploadLogo = async (file) => {
    if (!file) {
      return;
    }

    if (!file.type || !file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error("Logo image must be smaller than 8 MB.");
      return;
    }

    try {
      setUploadingLogo(true);

      /*
       * Local preview
       */
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      /*
       * Upload to existing backend
       */
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        "/admin/upload",
        formData
      );

      const uploadedUrl =
        response &&
        response.data &&
        response.data.url;

      if (!uploadedUrl) {
        throw new Error(
          "Upload completed but the server did not return a logo URL."
        );
      }

      /*
       * Store uploaded URL in the existing logo_url field
       */
      setF("logo_url", uploadedUrl);

      toast.success("Logo uploaded successfully.");
    } catch (error) {
      console.error("Logo upload error:", error);

      setLogoPreview(null);

      const errorDetail =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.detail;

      toast.error(
        formatApiError(errorDetail) ||
          error.message ||
          "Logo upload failed."
      );
    } finally {
      setUploadingLogo(false);
    }
  };

  /*
   * FILE INPUT CHANGE
   */
  const handleLogoChange = (event) => {
    const file =
      event &&
      event.target &&
      event.target.files &&
      event.target.files.length
        ? event.target.files[0]
        : null;

    if (file) {
      uploadLogo(file);
    }

    /*
     * Allows selecting the same file again
     */
    if (event && event.target) {
      event.target.value = "";
    }
  };

  /*
   * OPEN FILE PICKER
   */
  const openLogoPicker = () => {
    const input = document.getElementById(
      "website-logo-upload"
    );

    if (input) {
      input.click();
    }
  };

  const currentLogoUrl = s.logo_url || "";

  /*
   * GENERIC SETTINGS SECTION
   */
  const section = (title, fields) => (
    <Card className="border border-slate-200">
      <CardContent className="p-6">

        <div className="text-sm font-medium text-slate-700 mb-4">
          {title}
        </div>

        <div className="grid md:grid-cols-2 gap-4">

          {fields.map((field) => (
            <div
              key={field.key}
              className={
                field.full
                  ? "md:col-span-2"
                  : ""
              }
            >

              <Label>{field.label}</Label>

              {field.type === "textarea" ? (
                <Textarea
                  value={s[field.key] || ""}
                  onChange={(e) =>
                    setF(
                      field.key,
                      e.target.value
                    )
                  }
                  data-testid={
                    "settings-" + field.key
                  }
                />
              ) : (
                <Input
                  value={s[field.key] || ""}
                  onChange={(e) =>
                    setF(
                      field.key,
                      e.target.value
                    )
                  }
                  data-testid={
                    "settings-" + field.key
                  }
                />
              )}

            </div>
          ))}

        </div>

      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">

      {/* PAGE HEADER */}
      <div>

        <div className="text-xs uppercase tracking-[0.24em] text-amber-600">
          Manage
        </div>

        <h1 className="font-display text-3xl text-slate-950 mt-1">
          Website Settings
        </h1>

      </div>

      {/* COMPANY */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">

          <div className="text-sm font-medium text-slate-700 mb-4">
            Company
          </div>

          <div className="grid md:grid-cols-2 gap-4">

            {/* COMPANY NAME */}
            <div>

              <Label>Company Name</Label>

              <Input
                value={s.company_name || ""}
                onChange={(e) =>
                  setF(
                    "company_name",
                    e.target.value
                  )
                }
                data-testid="settings-company_name"
              />

            </div>

            {/* TAGLINE */}
            <div>

              <Label>Tagline</Label>

              <Input
                value={s.tagline || ""}
                onChange={(e) =>
                  setF(
                    "tagline",
                    e.target.value
                  )
                }
                data-testid="settings-tagline"
              />

            </div>

            {/* WEBSITE LOGO */}
            <div className="md:col-span-2">

              <Label>Website Logo</Label>

              <div className="mt-2 border border-slate-200 rounded-xl p-5 bg-slate-50">

                <div className="flex flex-col md:flex-row gap-5 md:items-center">

                  {/* LOGO PREVIEW */}
                  <div className="w-full md:w-48 h-32 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden">

                    {logoPreview || currentLogoUrl ? (
                      <img
                        src={
                          logoPreview ||
                          currentLogoUrl
                        }
                        alt="Website Logo Preview"
                        className="max-w-full max-h-full object-contain p-3"
                      />
                    ) : (
                      <div className="text-sm text-slate-400 text-center px-4">
                        No logo uploaded
                      </div>
                    )}

                  </div>

                  {/* UPLOAD CONTROLS */}
                  <div className="flex-1">

                    <div className="text-sm font-medium text-slate-700">
                      Upload your website logo
                    </div>

                    <div className="text-xs text-slate-500 mt-1">
                      PNG, JPG or WEBP. Maximum
                      file size: 8 MB.
                    </div>

                    <div className="mt-4 flex flex-wrap gap-3">

                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingLogo}
                        onClick={openLogoPicker}
                      >
                        {uploadingLogo
                          ? "Uploading..."
                          : "Choose Logo"}
                      </Button>

                      {currentLogoUrl ? (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={uploadingLogo}
                          onClick={() => {
                            setF(
                              "logo_url",
                              ""
                            );

                            setLogoPreview(null);

                            toast.success(
                              "Logo removed. Click Save changes to apply."
                            );
                          }}
                        >
                          Remove Logo
                        </Button>
                      ) : null}

                    </div>

                    {/* HIDDEN FILE INPUT */}
                    <input
                      id="website-logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />

                  </div>

                </div>

                {/* LOGO URL */}
                {currentLogoUrl ? (
                  <div className="mt-5">

                    <Label>Logo URL</Label>

                    <Input
                      value={currentLogoUrl}
                      onChange={(e) =>
                        setF(
                          "logo_url",
                          e.target.value
                        )
                      }
                      data-testid="settings-logo_url"
                      className="mt-1"
                    />

                    <p className="text-xs text-slate-500 mt-1">
                      This URL is saved with your
                      website settings.
                    </p>

                  </div>
                ) : null}

              </div>

            </div>

            {/* FAVICON */}
            <div>

              <Label>Favicon URL</Label>

              <Input
                value={s.favicon_url || ""}
                onChange={(e) =>
                  setF(
                    "favicon_url",
                    e.target.value
                  )
                }
                data-testid="settings-favicon_url"
              />

            </div>

          </div>

        </CardContent>
      </Card>

      {/* CONTACT */}
      {section("Contact", [
        {
          key: "phone_primary",
          label: "Primary Phone",
        },
        {
          key: "phone_secondary",
          label: "Secondary Phone",
        },
        {
          key: "whatsapp_number",
          label: "WhatsApp Number (with country code)",
        },
        {
          key: "email",
          label: "Email",
        },
        {
          key: "address",
          label: "Address",
          full: true,
          type: "textarea",
        },
        {
          key: "google_maps_url",
          label: "Google Maps URL",
          full: true,
        },
        {
          key: "business_hours",
          label: "Business Hours",
        },
      ])}

      {/* SOCIAL */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">

          <div className="text-sm font-medium text-slate-700 mb-4">
            Social
          </div>

          <div className="grid md:grid-cols-2 gap-4">

            {[
              "instagram",
              "facebook",
              "twitter",
              "youtube",
            ].map((key) => (
              <div key={key}>

                <Label>
                  {key.charAt(0).toUpperCase() +
                    key.slice(1)}
                </Label>

                <Input
                  value={
                    (s.social &&
                      s.social[key]) ||
                    ""
                  }
                  onChange={(e) =>
                    setSocial(
                      key,
                      e.target.value
                    )
                  }
                  data-testid={
                    "social-" + key
                  }
                />

              </div>
            ))}

          </div>

        </CardContent>
      </Card>

      {/* SEO */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">

          <div className="text-sm font-medium text-slate-700 mb-4">
            SEO
          </div>

          <div className="grid md:grid-cols-2 gap-4">

            {/* META TITLE */}
            <div>

              <Label>Meta Title</Label>

              <Input
                value={
                  (s.seo &&
                    s.seo.meta_title) ||
                  ""
                }
                onChange={(e) =>
                  setSeo(
                    "meta_title",
                    e.target.value
                  )
                }
                data-testid="seo-title"
              />

            </div>

            {/* KEYWORDS */}
            <div>

              <Label>Keywords</Label>

              <Input
                value={
                  (s.seo &&
                    s.seo.keywords) ||
                  ""
                }
                onChange={(e) =>
                  setSeo(
                    "keywords",
                    e.target.value
                  )
                }
                data-testid="seo-keywords"
              />

            </div>

            {/* META DESCRIPTION */}
            <div className="md:col-span-2">

              <Label>Meta Description</Label>

              <Textarea
                value={
                  (s.seo &&
                    s.seo.meta_description) ||
                  ""
                }
                onChange={(e) =>
                  setSeo(
                    "meta_description",
                    e.target.value
                  )
                }
                data-testid="seo-description"
              />

            </div>

            {/* OG IMAGE */}
            <div className="md:col-span-2">

              <Label>OG Image URL</Label>

              <Input
                value={
                  (s.seo &&
                    s.seo.og_image) ||
                  ""
                }
                onChange={(e) =>
                  setSeo(
                    "og_image",
                    e.target.value
                  )
                }
                data-testid="seo-og"
              />

            </div>

          </div>

        </CardContent>
      </Card>

      {/* SAVE BUTTON */}
      <div className="flex justify-end">

        <Button
          className="bg-slate-950 hover:bg-slate-800"
          disabled={
            save.isPending ||
            uploadingLogo
          }
          onClick={() =>
            save.mutate(
              form || data || {}
            )
          }
          data-testid="settings-save-btn"
        >
          {save.isPending
            ? "Saving..."
            : "Save changes"}
        </Button>

      </div>

    </div>
  );
}